/**
 * Drawer host — fans the active named drawer out to its content panel.
 * Each panel mirrors its RN counterpart (ItineraryOverlay, ExploreOverlay,
 * DealsOverlay, MessagesOverlay/ChatLog, SettingsDrawer).
 */
import React from 'react';
import { useDrawer } from '../../context/DrawerContext';
import { useLanguage } from '../../hooks/useLanguage';
import Drawer from '../../common/Drawer';
import ProfilePanel from './ProfilePanel';
import DealsContent from './DealsContent';
import ItineraryContent from './ItineraryContent';
import ExploreContent from './ExploreContent';
import ChatContent from './ChatContent';
import InboxContent from './InboxContent';
import LanguageContent from './LanguageContent';
import AgentSwitcherContent from './AgentSwitcherContent';
import WalletContent from './WalletContent';
import GuestSignupContent from './GuestSignupContent';
import { useGuestUX } from '@wg/hooks/guest';
import { useAuthContext } from '@wg/hooks/auth';

const DrawerHost: React.FC = () => {
  const { currentDrawer, closeDrawer } = useDrawer();
  const { t } = useLanguage();
  const { isGuest } = useAuthContext();
  // Pre-warm the guest content fetch so the drawer's text renders instantly
  // on tap. Cheap (one cached request) and aligns with RN's eager fetch.
  useGuestUX({ isGuest, language: undefined });

  return (
    <>
      <Drawer
        open={currentDrawer === 'itinerary'}
        onClose={closeDrawer}
        title={t('ITINERARY') || 'Itinerary'}
        variant="full"
      >
        <ItineraryContent />
      </Drawer>

      <Drawer
        open={currentDrawer === 'explore'}
        onClose={closeDrawer}
        title={t('EXPLORE') || 'Explore'}
        variant="full"
      >
        <ExploreContent />
      </Drawer>

      {/* RN's BookOverlay = ExploreOverlay locked to discover mode. */}
      <Drawer
        open={currentDrawer === 'book'}
        onClose={closeDrawer}
        title={t('BOOK') || 'Book'}
        variant="full"
      >
        <ExploreContent lockedMode="discover" />
      </Drawer>

      <Drawer
        open={currentDrawer === 'deals'}
        onClose={closeDrawer}
        title={t('DEALS_AND_REWARDS') || 'Deals & Rewards'}
        variant="full"
      >
        <DealsContent />
      </Drawer>

      <Drawer
        open={currentDrawer === 'chat'}
        onClose={closeDrawer}
        title={t('CHAT') || 'Chat'}
        variant="full"
      >
        <ChatContent visible={currentDrawer === 'chat'} />
      </Drawer>

      <Drawer
        open={currentDrawer === 'inbox'}
        onClose={closeDrawer}
        title={t('INBOX') || 'Inbox'}
        variant="full"
      >
        <InboxContent />
      </Drawer>

      <Drawer
        open={currentDrawer === 'wallet'}
        onClose={closeDrawer}
        title={t('VOICE_TIME') || 'Voice time'}
        variant="full"
      >
        <WalletContent />
      </Drawer>

      <Drawer
        open={currentDrawer === 'language'}
        onClose={closeDrawer}
        title={t('LANGUAGE') || 'Language'}
        variant="full"
      >
        <LanguageContent />
      </Drawer>

      <Drawer
        open={currentDrawer === 'agentSwitcher'}
        onClose={closeDrawer}
        title={t('AGENT_SELECTOR_TITLE') || 'Your guides'}
        variant="full"
      >
        <AgentSwitcherContent visible={currentDrawer === 'agentSwitcher'} />
      </Drawer>

      <Drawer
        open={currentDrawer === 'search'}
        onClose={closeDrawer}
        title={t('SEARCH') || 'Search'}
      >
        <div
          style={{
            padding: '40px 0',
            textAlign: 'center',
            color: 'rgba(255,255,255,0.7)',
          }}
        >
          {t('SEARCH_ACROSS_YOUR_TRIP') || 'Search across your trip.'}
        </div>
      </Drawer>

      <Drawer
        open={currentDrawer === 'settings'}
        onClose={closeDrawer}
        title={t('PROFILE') || 'Profile'}
        variant="full"
      >
        <ProfilePanel />
      </Drawer>

      <Drawer
        open={currentDrawer === 'guestSignup'}
        onClose={closeDrawer}
        title={t('UNLOCK_WITH_FREE_ACCOUNT')}
        variant="bottom-sheet"
      >
        <GuestSignupContent />
      </Drawer>
    </>
  );
};

export default DrawerHost;
